import { Pipe, PipeTransform } from "@angular/core";
import { IApiExpense, IApiExpenseItem, IApiTimeEntryType } from "../modules/graphql/types/types";

export type QuantityType = "billable" | "nonBillable" | "both";

@Pipe({
  name: "visibleExpenseItems"
})
export class VisibleExpenseItemsPipe implements PipeTransform {
  transform(expenseItems: IApiExpenseItem[], unlinked: boolean, exclude: boolean = true, searchKeyword: string = ''): IApiExpenseItem[] {
    if (expenseItems?.length) {
      const excludeNames = ['Mileage', 'Annual Evidence Storage', 'Photographs', 'Lightening Strike Report'];
      expenseItems = exclude ? expenseItems.filter((item) => item?.AppliesTo?.name !== (unlinked ? "Investigation" : "Unlinked") && !excludeNames?.includes(item?.name) && item.active === true) : expenseItems.filter((item) => item?.AppliesTo?.name !== (unlinked ? "Investigation" : "Unlinked") && item.name !== 'Mileage' && item.active === true);
      if (searchKeyword) {
        const regex = /:\s(?:\(CA\)\s)?(.*)/;
        return expenseItems.filter((item) => {
          const text = item['name'] || '';
          const match = text.match(regex);
          if (match) {
            /* search by after colon OR colon (CA) */
            const partAfterColon = match[1] || '';
            return partAfterColon.trim().toLowerCase().startsWith(searchKeyword?.trim().toLowerCase());
          } else {
            /* search with startsWith */
            return text.trim().toLowerCase().startsWith(searchKeyword?.trim().toLowerCase());
          }
        });
      } else {
        /* default */
        return expenseItems;
      }
    } else {
      return [];
    }
  }
}


@Pipe({
  name: "visibleTimeEntryItems"
})
export class VisibleTimeEntryItemsPipe implements PipeTransform {
  transform(timeEntryItems: IApiTimeEntryType[], unlinked: boolean, searchKeyword: string): IApiTimeEntryType[] {
    if (timeEntryItems?.length) {
      if (searchKeyword) {
        const regex = /:\s(?:\(CA\)\s)?(.*)/;
        return timeEntryItems.filter((item) => {
          const text = item['name'] || '';
          const match = text.match(regex);
          if (match) {
            /* search by after colon OR colon (CA) */
            const partAfterColon = match[1] || '';
            return partAfterColon.trim().toLowerCase().startsWith(searchKeyword?.trim().toLowerCase()) && item?.appliesTo !== (unlinked ? "Investigation" : "Unlinked") && item.name !== 'Mileage' && item.activated === true;
          } else {
            /* search with startsWith */
            return text.trim().toLowerCase().startsWith(searchKeyword?.trim().toLowerCase()) && item?.appliesTo !== (unlinked ? "Investigation" : "Unlinked") && item.name !== 'Mileage' && item.activated === true;
          }
        });
      } else {
        /* default */
        return timeEntryItems.filter((item) => item?.appliesTo !== (unlinked ? "Investigation" : "Unlinked") && item.name !== 'Mileage' && item.activated === true);
      }
    } else {
      return [];
    }
  }
}

@Pipe({
  name: "reimbursementRate"
})
export class ReimbursementRatePipe implements PipeTransform {
  transform(expense: IApiExpense, type: QuantityType = "both"): any {
    const expenseDate = new Date(expense?.expenseDate);
    const rates = expense?.ExpenseItem?.ExpenseReimbursementRate ?? [];

    const rate =
      expenseDate.getFullYear() >= 2023
        ? rates
          .filter((r) => new Date(r.year) <= expenseDate)
          .sort(
            (a, b) =>
              new Date(b.year).getTime() - new Date(a.year).getTime()
          )[0]
        : rates.find((r) => new Date(r.year).getFullYear() === 2023);

    const reimbursementRate =
      rate?.reimbursementRate || expense?.ExpenseItem?.reimbursementRate;

    // Get selected quantity based on type
    let qty = 0;
    switch (type) {
      case "billable":
        qty = expense?.billableQuantity ?? 0;
        break;
      case "nonBillable":
        qty = expense?.nonBillableQuantity ?? 0;
        break;
      case "both":
      default:
        qty = (expense?.billableQuantity ?? 0) + (expense?.nonBillableQuantity ?? 0);
        break;
    }

    if (expense?.ExpenseItem?.name === "Drone Usage") {
      // For Drone Usage qty is always 1 regardless of type
      return type === 'nonBillable' ? 0 : reimbursementRate;
    }

    return qty * reimbursementRate;
  }
}

@Pipe({
  name: "expenseDisplayForSelf"
})
export class ExpenseDisplayPipe implements PipeTransform {
  constructor(private reimbursementPipe: ReimbursementRatePipe) { }

  transform(dataItem: any, type: QuantityType = "both"): string {
    // compute qty based on type
    let qty = 0;
    switch (type) {
      case "billable":
        qty = dataItem?.billableQuantity ?? 0;
        break;
      case "nonBillable":
        qty = dataItem?.nonBillableQuantity ?? 0;
        break;
      case "both":
      default:
        qty =
          (dataItem?.billableQuantity ?? 0) +
          (dataItem?.nonBillableQuantity ?? 0);
        break;
    }

    switch (dataItem?.ExpenseItem?.name) {
      case "Mileage":
        return `${qty} mi`;
      case "Drone Usage": {
        const value = this.reimbursementPipe.transform(dataItem, type);
        return value ? `$${this.reimbursementPipe.transform(dataItem, type)?.toFixed(2)}` : '-';
      }
      default:
        return `$${qty?.toFixed(2)}`;
    }
  }
}
@Pipe({
  name: "expenseDisplayForSelfAmount"
})
export class ExpenseAmountDisplayPipe implements PipeTransform {
  constructor(private reimbursementPipe: ReimbursementRatePipe) { }

  transform(expense: IApiExpense): number {
    if (expense?.ExpenseItem?.name === "Mileage" || expense?.ExpenseItem?.name === "Drone Usage") {
      return this.reimbursementPipe.transform(expense);
    }

    return (expense?.billableQuantity || 0) + (expense?.nonBillableQuantity || 0);
  }
}
